import { useCallback } from 'react';

import { isBefore } from 'date-fns';
import { isNil } from 'lodash-es';

import type { AssetsSegmentSchemaWithIdPatched } from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import { Button, ButtonBase, Chip, Icon, Text } from '@iconik-io/uad';

import type { SegmentEntityWithIdNormalized } from '../../../../redux/entities/normalized/segment';
import type { TimecodeOptions } from '../../../../utils/get_proxy_timecode_options';
import { useAppPlayerContext } from '../player/context';
import { TimecodeField } from '../ui/form_fields/timecode_field/timecode_field';
import { pdfSegmentRangeThresholdDate } from './constants';

import styles from './segment_range.module.css';

type SegmentTimecodeProps = {
  segment: AssetsSegmentSchemaWithIdPatched | SegmentEntityWithIdNormalized;
  mode: 'page' | 'smpte';
  timecodeOptions: TimecodeOptions | undefined;
};

export function SegmentRange({
  timecodeOptions,
  segment,
  mode,
}: SegmentTimecodeProps) {
  // TODO move selectIsTimeBasedAsset logic to shared-lib https://linear.app/iconik/issue/RP-1525
  // const timeBased = useAppSelector((state) =>
  //   selectIsTimeBasedAsset(state, assetId),
  // );
  const timeBased = false;
  const { store: unifiedPlayerStore } = useAppPlayerContext();

  const handlePlayClick = () => {
    unifiedPlayerStore.segment.setSelectedSegmentIds([segment.id]);
    unifiedPlayerStore.playableMedia.play();
  };

  const handleSegmentClick = useCallback(() => {
    unifiedPlayerStore.segment.setSelectedSegmentIds([segment.id]);
  }, [segment]);

  let timeStart = segment.time_start_milliseconds;
  let timeEnd = segment.time_end_milliseconds;

  if (!timeStart && timeBased && !segment.parent_id) {
    timeStart = 0;
  }

  if (!timeEnd && timeBased && !segment.parent_id) {
    timeEnd = 0;
  }

  if (
    isNil(segment.time_start_milliseconds) &&
    isNil(segment.time_end_milliseconds)
  ) {
    return null;
  }

  // Checking if timeStart is defined for page mode, timeStart and timeEnd will
  // have the same value.
  if (mode === 'page' && !isNil(timeStart)) {
    let pageText = `${timeStart + 1}`;
    // Beta PDF pages hack, see pdfSegmentRangeThresholdDate definition for more details.
    if (
      segment.date_created &&
      isBefore(new Date(segment.date_created), pdfSegmentRangeThresholdDate) &&
      timeStart >= 1000
    ) {
      pageText = `${timeStart / 1000}`;
    }

    return (
      <Chip size="xs" color="default" aria-label="Segment page number">
        <Text color="high_emphasis" variant="body">
          Page {pageText}
        </Text>
      </Chip>
    );
  }

  if (!timecodeOptions) {
    return null;
  }
  return (
    <Chip
      size="xs"
      color="default"
      className={styles.timecode_buttons}
      aria-label="Timecode"
      icon={
        timeStart !== timeEnd ? (
          <Button
            onPress={handlePlayClick}
            round
            icon={<Icon name="player_play" />}
            size="s"
            variant="tertiary"
          />
        ) : null
      }
    >
      <ButtonBase onPress={handleSegmentClick}>
        <TimecodeField
          timecodeOptions={timecodeOptions}
          value={Number(timeStart)}
          disabled
        />
        {timeStart !== timeEnd && (
          <>
            -
            <TimecodeField
              timecodeOptions={timecodeOptions}
              value={Number(timeEnd)}
              disabled
            />
          </>
        )}
      </ButtonBase>
    </Chip>
  );
}
